import React from "react";

import costEstimatorLogo from "../../assets/cost-estimator.svg";
import machineCatalogLogo from "../../assets/machine-catalog.svg";
import dataMinerLogo from "../../assets/data-miner.svg";

import { AskAiIcon, HomeIcon, LogOutIcon, ProfileIcon } from "./SideNavIcons";

import styles from "./SideNav.module.css";

/**
 * Base URL of the ICE shell, which serves Home, ICE, IMC and Ask Ironworks AI.
 * IDM is deployed separately, so every rail link is cross-origin and opens in a new tab.
 */
const iceBaseUrl = (import.meta.env.VITE_ICE_BASE_URL ?? "").replace(/\/+$/, "");

if (!iceBaseUrl) {
    // Without a base URL a relative href would navigate inside IDM instead of ICE.
    // The items still render, disabled, so a misconfigured deploy is visible rather than silent.
    console.warn("VITE_ICE_BASE_URL is not set — ICE links in the side navigation are disabled.");
}

const targetWindowName = (key: string) => `ironworks-${key}`;

type RailItem = {
    key: string;
    /** Tooltip and accessible name. ICE spells the app names out here and abbreviates in the label. */
    title: string;
    /** Path on the ICE host. Omitted for IDM, which is this app. */
    path?: string;
    label?: string;
    logo?: string;
    icon?: React.ReactNode;
};

/** Same order as the ICE rail: Home, ICE, IMC, IDM, Ask Ironworks AI. */
const railItems: RailItem[] = [
    { key: "home", title: "Home", path: "/home", label: "Home", icon: <HomeIcon className={styles.homeIcon} /> },
    { key: "ice", title: "Cost Estimator (ICE)", path: "/", label: "ICE", logo: costEstimatorLogo },
    { key: "imc", title: "Machine Catalog (IMC)", path: "/imc", label: "IMC", logo: machineCatalogLogo },
    { key: "idm", title: "Data Miner (IDM)", label: "IDM", logo: dataMinerLogo },
    { key: "ask-ironworks-ai", title: "Ask Ironworks AI", path: "/ask-ironworks-ai", label: "Ask\nIronworks AI", icon: <AskAiIcon /> }
];

const ItemContent = ({ item }: { item: RailItem }) => (
    <>
        {item.logo ? (
            <span className={styles.logoWrap}>
                <img src={item.logo} alt="" className={styles.logo} />
            </span>
        ) : (
            <span className={styles.iconWrap}>{item.icon}</span>
        )}
        {item.label && <span className={styles.label}>{item.label}</span>}
    </>
);

type SideNavProps = {
    onProfileClick: () => void;
    onLogOutClick: () => void;    
    showProfile?: boolean;
};

export const SideNav = ({ onProfileClick, onLogOutClick, showProfile = true }: SideNavProps) => (
    <nav className={styles.rail} aria-label="Ironworks applications">
        <div className={styles.group}>
            {railItems.map(item => {
                // IDM is the current app: rendered as the active, non-interactive item, as ICE does for itself.
                if (!item.path) {
                    return (
                        <div key={item.key} className={styles.itemWrap}>
                            <div
                                className={`${styles.item} ${styles.itemActive}`}
                                role="link"
                                aria-disabled="true"
                                title={item.title}
                                aria-label={item.title}
                                aria-current="page"
                            >
                                <ItemContent item={item} />
                            </div>
                        </div>
                    );
                }

                // The label is hidden below 640px, so the title is always the accessible name.
                if (!iceBaseUrl) {
                    return (
                        <div key={item.key} className={styles.itemWrap}>
                            <div
                                className={`${styles.item} ${styles.itemDisabled}`}
                                role="link"
                                aria-disabled="true"
                                title={`${item.title} — unavailable, ICE base URL is not configured`}
                                aria-label={`${item.title} — unavailable, ICE base URL is not configured`}
                            >
                                <ItemContent item={item} />
                            </div>
                        </div>
                    );
                }

                return (
                    <div key={item.key} className={styles.itemWrap}>
                        <a
                            className={styles.item}
                            href={`${iceBaseUrl}${item.path}`}
                            target={targetWindowName(item.key)}
                            rel="noopener noreferrer"
                            title={item.title}
                            aria-label={item.title}
                        >
                            <ItemContent item={item} />
                        </a>
                    </div>
                );
            })}
        </div>

        <div className={styles.spacer} />

        <div className={styles.footer}>
            {showProfile && (
                <div className={styles.itemWrap}>
                    <button type="button" className={`${styles.item} ${styles.itemButton}`} title="Profile" aria-label="Profile" onClick={onProfileClick}>
                        <span className={styles.iconWrap}>
                            <ProfileIcon />
                        </span>
                        <span className={styles.label}>Profile</span>
                    </button>
                </div>
            )}
            <div className={styles.itemWrap}>
                <button type="button" className={`${styles.item} ${styles.itemButton}`} title="Log Out" aria-label="Log Out" onClick={onLogOutClick}>
                    <span className={styles.iconWrap}>
                        <LogOutIcon />
                    </span>
                    <span className={styles.label}>Log Out</span>
                </button>
            </div>
        </div>
    </nav>
);
